import ScButton from "@/src/components/ui-kit/button";
import ScInput from "@/src/components/ui-kit/input";
import { useCreateRoleMutation } from "@/src/services/uam/permissions.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";

type CreateRoleModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
}>;

// Slug the backend expects: lowercase, alphanumerics joined by single hyphens.
const toSlug = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const CreateRoleModal = ({ visible, onClose }: CreateRoleModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const { mutate: createRole, isPending } = useCreateRoleMutation();

  const slug = toSlug(name);
  const canSubmit = slug.length > 0 && !isPending;

  const reset = () => {
    setName("");
    setDescription("");
  };

  const handleClose = () => {
    if (isPending) return;
    reset();
    onClose();
  };

  const handleCreate = () => {
    if (!canSubmit) return;
    createRole(
      { name: name.trim(), description: description.trim(), slug },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable
        style={varStyle}
        className="flex-1 justify-center items-center bg-shadow-dark px-6"
        onPress={handleClose}
      >
        <Pressable
          className="w-full max-w-md bg-ash rounded-2xl p-6 gap-4"
          onPress={(e) => e.stopPropagation()}
        >
          <View className="flex-row justify-between items-center">
            <Text className="flex-1 text-lg md:text-xl font-semibold text-onyx">
              {t("LABEL_CREATE_ROLE")}
            </Text>
            <Pressable
              onPress={handleClose}
              hitSlop={8}
              className="rounded-full hover:bg-ash-3"
            >
              <CloseCircle color={themeVars["--color-primary-default"]} />
            </Pressable>
          </View>

          <ScInput
            label="LABEL_ROLE_NAME"
            placeholder="LABEL_ENTER_ROLE_NAME"
            value={name}
            onChangeText={setName}
          />
          <ScInput
            label="LABEL_ROLE_DESCRIPTION"
            placeholder="LABEL_ENTER_ROLE_DESCRIPTION"
            value={description}
            onChangeText={setDescription}
          />

          {slug.length > 0 && (
            <Text className="text-xs text-ash-2">
              {t("LABEL_ROLE_SLUG")}: {slug}
            </Text>
          )}

          <View className="flex-row justify-end gap-3 mt-2">
            <ScButton
              text="BTN_CANCEL"
              mode="outlined"
              onPress={handleClose}
              disabled={isPending}
            />
            <ScButton
              text="BTN_CREATE"
              onPress={handleCreate}
              loading={isPending}
              disabled={!canSubmit}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default CreateRoleModal;
